import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../ThemeContext";
import {apiGet, apiDownloadFile, apiDelete} from "../../utils/apiClient";
import HtmlPreviewViewer from "../../components/HtmlPreviewViewer";

interface Report {
  report_id: string;
  version_id: string;
  report_name: string;
  version: string;
  created_at: string;
  created_by: string;
  sections_count: number;
  html_url: string | null;
  pdf_url: string | null;
}

// Simple icons
const Icons = {
  File: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
  Download: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Eye: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Edit: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  Plus: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  X: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Back: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  Spinner: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round"/></svg>,
};

export default function ReportsDirectory() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "preview">("list");
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null); // report_id currently loading preview
  const [loadingDownload, setLoadingDownload] = useState<string | null>(null); // report_id currently downloading

  // Load reports on mount
  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await apiGet('/api/reporting/reports');
      const data = await response.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report: Report) => {
    setLoadingDownload(report.report_id);
    try {
      // Always generate a fresh PDF on-demand using the report_id
      await apiDownloadFile(
        `/api/reporting/reports/download/pdf/${report.report_id}`,
        undefined, // No body needed, report_id is in the URL
        `${report.report_name}.pdf`
      );
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      alert(`Failed to download PDF: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingDownload(null);
    }
  };

  const handleView = async (report: Report) => {
    if (report.html_url) {
      setLoadingPreview(report.report_id);
      try {
        // Fetch HTML content with authentication
        const response = await apiGet(report.html_url);
        if (!response.ok) {
          throw new Error('Failed to load preview');
        }
        const htmlContent = await response.text();
        
        // Store the HTML content in the report object for display
        setSelectedReport({ ...report, htmlContent } as any);
        setViewMode("preview");
      } catch (error: any) {
        console.error('Failed to load preview:', error);
        alert(`Failed to load preview: ${error.message || 'Unknown error'}`);
      } finally {
        setLoadingPreview(null);
      }
    } else {
      alert('No HTML preview available for this report');
    }
  };

  const handleEdit = (report: Report) => {
    navigate(`/reports/builder?report_id=${report.report_id}`);
  };

  const handleDelete = async (report: Report) => {
    if (!window.confirm(`Delete "${report.report_name}"? This will remove all versions and cannot be undone.`)) return;
    try {
      const response = await apiDelete(`/api/reporting/reports/saved/${report.report_id}`);
      if (response.ok) {
        setReports(r => r.filter(x => x.report_id !== report.report_id));
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`Failed to delete: ${data.detail || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Error deleting report: ${error.message}`);
    }
  };

  const handleNewReport = () => {
    navigate('/reports/builder');
  };

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (viewMode === "preview" && selectedReport) {
    return (
      <HtmlPreviewViewer
        htmlContent={(selectedReport as any).htmlContent || ''}
        theme={theme}
        onBack={() => setViewMode("list")}
        onDownload={() => handleDownload(selectedReport)}
        title={selectedReport.report_name}
        subtitle={`Version ${selectedReport.version} · ${formatDate(selectedReport.created_at)}`}
        showBackButton={true}
        showDownloadButton={true}
        downloadButtonLabel="Download PDF"
        minHeight="100vh"
      />
    );
  }

  return (
    <div style={{ background: theme.background, color: theme.text, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: `1px solid ${theme.border}`, background: theme.navbarBackground, position: "sticky", top: 0, zIndex: 40, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 30, height: 30, borderRadius: 7, background: `linear-gradient(135deg, ${theme.blue}, ${theme.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "white" }}>R</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>Reports Directory</div>
            <div style={{ fontSize: 13, color: theme.textSecondary }}>{reports.length} report version{reports.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <button onClick={handleNewReport} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 14px", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", background: theme.buttonBackground, color: theme.buttonText }}><Icons.Plus /> New Report</button>
      </header>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "20px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: theme.textSecondary }}>Loading reports...</div>
        ) : reports.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: theme.text }}>No Reports Yet</h3>
            <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 20 }}>Create your first report to get started</p>
            <button onClick={handleNewReport} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: "pointer", border: "none", background: theme.buttonBackground, color: theme.buttonText }}><Icons.Plus /> Create Report</button>
          </div>
        ) : (
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ display: "grid", gap: 12 }}>
              {reports.map((report) => (
                <div key={report.report_id} style={{ background: theme.cardBackground, borderRadius: 10, padding: "16px 20px", border: `1px solid ${theme.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: theme.infoLight, display: "flex", alignItems: "center", justifyContent: "center", color: theme.info, fontSize: 18 }}>
                      📊
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2, color: theme.text }}>{report.report_name}</div>
                      <div style={{ fontSize: 13, color: theme.textSecondary }}>
                        Version {report.version} · {formatDate(report.created_at)} · {report.sections_count} section{report.sections_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button 
                      onClick={() => handleView(report)} 
                      disabled={loadingPreview === report.report_id}
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 4, 
                        padding: "6px 12px", 
                        borderRadius: 7, 
                        fontSize: 13, 
                        fontWeight: 600, 
                        cursor: loadingPreview === report.report_id ? "not-allowed" : "pointer", 
                        border: `1px solid ${theme.border}`, 
                        background: theme.background, 
                        color: theme.text,
                        opacity: loadingPreview === report.report_id ? 0.6 : 1
                      }}
                    >
                      {loadingPreview === report.report_id ? <Icons.Spinner /> : <Icons.Eye />} 
                      {loadingPreview === report.report_id ? "Loading..." : "Preview"}
                    </button>
                    <button 
                      onClick={() => handleDownload(report)} 
                      disabled={loadingDownload === report.report_id}
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 4, 
                        padding: "6px 12px", 
                        borderRadius: 7, 
                        fontSize: 13, 
                        fontWeight: 600, 
                        cursor: loadingDownload === report.report_id ? "not-allowed" : "pointer", 
                        border: `1px solid ${theme.border}`, 
                        background: theme.background, 
                        color: theme.text,
                        opacity: loadingDownload === report.report_id ? 0.6 : 1
                      }}
                    >
                      {loadingDownload === report.report_id ? <Icons.Spinner /> : <Icons.Download />} 
                      {loadingDownload === report.report_id ? "Downloading..." : "Download"}
                    </button>
                    <button onClick={() => handleEdit(report)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", background: theme.buttonBackground, color: theme.buttonText }}>
                      <Icons.Edit /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(report)}
                      title="Delete report"
                      style={{ display: "flex", alignItems: "center", padding: "6px 10px", borderRadius: 7, fontSize: 13, cursor: "pointer", border: `1px solid ${theme.border}`, background: theme.background, color: theme.textSecondary }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = theme.error; e.currentTarget.style.color = theme.error; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textSecondary; }}
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
